import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { darkColors } from '../constants/colors';

type Props = {
  label: string;
  decorative?: boolean;
};

function toneFromLabel(label: string) {
  const normalized = label.trim().toLowerCase();
  if (
    normalized.includes('present') ||
    normalized.includes('checked in') ||
    normalized.includes('day completed') ||
    normalized.includes('approved')
  ) {
    return 'success';
  }
  if (
    normalized.includes('late') ||
    normalized.includes('pending') ||
    normalized.includes('warning')
  ) {
    return 'warning';
  }
  if (
    normalized.includes('absent') ||
    normalized.includes('rejected') ||
    normalized.includes('failed')
  ) {
    return 'danger';
  }
  if (normalized.includes('wfh') || normalized.includes('working from home')) {
    return 'info';
  }
  if (normalized.includes('leave')) {
    return 'leave';
  }
  return 'neutral';
}

export function AttendanceStatusBadge({ label, decorative = false }: Props) {
  const palette = useAppColors();
  const isDark = palette.background === darkColors.background;
  const tone = toneFromLabel(label);

  const toneStyle = useMemo(() => {
    switch (tone) {
      case 'success':
        return {
          backgroundColor: isDark ? '#054F31' : '#DCFCE7',
          color: isDark ? '#6CE9A6' : '#067647',
        };
      case 'warning':
        return {
          backgroundColor: isDark ? '#7A2E0E' : '#FEF0C7',
          color: isDark ? '#FEC84B' : '#B54708',
        };
      case 'danger':
        return {
          backgroundColor: isDark ? '#7A271A' : '#FEE4E2',
          color: isDark ? '#FDA29B' : '#B42318',
        };
      case 'info':
        return {
          backgroundColor: isDark ? '#102A56' : '#E0EAFF',
          color: isDark ? '#84ADFF' : '#175CD3',
        };
      case 'leave':
        return {
          backgroundColor: isDark ? '#3B1C5C' : '#F4EBFF',
          color: isDark ? '#D6BBFB' : '#6941C6',
        };
      default:
        return {
          backgroundColor: palette.border,
          color: palette.textPrimary,
        };
    }
  }, [isDark, palette.border, palette.textPrimary, tone]);

  return (
    <View
      accessibilityElementsHidden={decorative}
      importantForAccessibility={decorative ? 'no-hide-descendants' : 'auto'}
      accessibilityRole={decorative ? undefined : 'text'}
      accessibilityLabel={decorative ? undefined : `Status ${label}`}
      style={[styles.badge, { backgroundColor: toneStyle.backgroundColor }]}
    >
      <Text style={[styles.text, { color: toneStyle.color }]}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  text: {
    ...typography.caption,
    fontWeight: '700',
  },
});
